import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3 } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { assistentApi, type AssistentVerbrauch as Verbrauch } from '@/lib/assistent/assistent.api'
import { cardTitle, formCard, inlineInput } from '@/lib/form-styles'

const zahl = (n: number) => n.toLocaleString('de-DE')

/** Der laufende Monat und die elf davor als `JJJJ-MM` — so weit reicht die Aufbewahrung des Verbrauchs sicher. */
function monate(jetzt = new Date()): string[] {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(jetzt.getUTCFullYear(), jetzt.getUTCMonth() - i, 1))
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
  })
}

/**
 * AP-18 12.2 (Phase G) — Karte **„Verbrauch"** des Assistenten: Tokens und Zahl der Fragen je
 * Benutzer in einem Monat. Der Administrator sieht Zahlen, **nie** Inhalte der Gespräche (AS-9).
 *
 * `tagesgrenze` färbt, wer heute an seiner Grenze ist. Die Liste ist so lang wie die Zahl der
 * Benutzer, die den Assistenten in diesem Monat benutzt haben — eine Übersicht in der Karte, keine
 * Auswertungsseite.
 */
export function AssistentVerbrauch({ tagesgrenze }: { tagesgrenze: number | null }) {
  const { t, i18n } = useTranslation()
  const [auswahl] = useState(() => monate())
  const [monat, setMonat] = useState(auswahl[0])
  const [v, setV] = useState<Verbrauch | null>(null)
  const [fehler, setFehler] = useState<unknown>(null)

  useEffect(() => {
    let weg = false
    setFehler(null)
    void assistentApi
      .verbrauch(monat)
      .then((d) => {
        if (!weg) setV(d)
      })
      .catch((x) => {
        if (!weg) setFehler(x)
      })
    return () => {
      weg = true
    }
  }, [monat])

  const monatName = (p: string) => new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'de-AT', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${p}-01T00:00:00Z`))

  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-primary" />
          <h2 className={cardTitle}>{t('assistant.usage.title')}</h2>
        </div>
        <select className={`${inlineInput} !w-auto`} aria-label={t('assistant.usage.month')} value={monat} onChange={(x) => setMonat(x.target.value)}>
          {auswahl.map((p) => (
            <option key={p} value={p}>
              {monatName(p)}
            </option>
          ))}
        </select>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{t('assistant.usage.description')}</p>

      {fehler ? <p className="mt-3 text-sm text-destructive">{apiErrorText(fehler, t('assistant.usage.loadError'))}</p> : null}
      {v && v.users.length === 0 && <p className="mt-3 text-sm text-muted-foreground">{t('assistant.usage.empty')}</p>}
      {v && v.users.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">{t('assistant.usage.user')}</th>
                <th className="py-1.5 pr-3 text-right font-medium">{t('assistant.usage.requests')}</th>
                <th className="py-1.5 pr-3 text-right font-medium">{t('assistant.usage.tokens')}</th>
                {v.current && <th className="py-1.5 text-right font-medium">{t('assistant.usage.today')}</th>}
              </tr>
            </thead>
            <tbody>
              {v.users.map((z) => {
                const anGrenze = v.current && tagesgrenze !== null && z.todayTokens >= tagesgrenze
                return (
                  <tr key={z.userId} className="border-b border-border/50">
                    <td className="py-1.5 pr-3">{z.name}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{zahl(z.requests)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{zahl(z.totalTokens)}</td>
                    {v.current && (
                      <td className={`py-1.5 text-right tabular-nums ${anGrenze ? 'font-medium text-amber-700 dark:text-amber-300' : ''}`} title={anGrenze ? t('assistant.usage.atLimit') : undefined}>
                        {zahl(z.todayTokens)}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="font-medium">
                <td className="py-1.5 pr-3">{t('assistant.usage.total')}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{zahl(v.total.requests)}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{zahl(v.total.totalTokens)}</td>
                {v.current && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
